/**
 * Carrier Advance Form — carrier advance / partial-payment ledger.
 *
 * Records undisputed carrier advances against a claim and derives a
 * read-only "Amount in Controversy" = net claim − sum(advances).
 * Cloned structurally from PayrollAdjustmentForm.tsx (Task 8).
 *
 * HARD CONSTRAINT: Amount in Controversy is a DERIVED, read-only
 * figure fetched from GET /adjustments/carrier-advances/{id}/controversy.
 * It is never the filed claim, never editable here, and this
 * component performs zero writes to any claim/net_claim field —
 * only to the advances ledger itself (drafts.data.carrier_advances[]).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Loader2,
  Plus,
  Trash2,
  AlertCircle,
  Scale,
} from 'lucide-react';

import api from '../../utils/api';
import { FinancialValue } from '../shared/FinancialValue';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CarrierAdvance {
  advance_id: string;
  amount_cents: number;
  payment_date: string;
  reference: string;
  description: string;
  undisputed: boolean;
  author_id: string;
  author_name: string;
  org_id: string;
  created_at: string;
}

interface ListResponse {
  report_id: string;
  advances: CarrierAdvance[];
  total_cents: number;
}

interface ControversyResponse {
  report_id: string;
  net_claim: number | null;
  advances_total: number;
  amount_in_controversy: number | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface Props {
  reportId: string;
}

const BASE = '/api/v1/adjustments/carrier-advances';

export default function CarrierAdvanceForm({ reportId }: Props) {
  const [data, setData] = useState<ListResponse | null>(null);
  const [controversy, setControversy] = useState<ControversyResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [paymentDate, setPaymentDate] = useState('');
  const [amountDollars, setAmountDollars] = useState('');
  const [reference, setReference] = useState('');
  const [description, setDescription] = useState('');
  // Default UNCHECKED — "undisputed" is a legally material attestation, so it must
  // be an affirmative human act, not a pre-checked default (K3 review 2026-08-24).
  const [undisputed, setUndisputed] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [listResp, controversyResp] = await Promise.all([
        api.get<ListResponse>(`${BASE}/${reportId}`),
        api.get<ControversyResponse>(`${BASE}/${reportId}/controversy`),
      ]);
      setData(listResp);
      setControversy(controversyResp);
    } catch (err: any) {
      setLoadError(err?.message || 'Failed to load carrier advances.');
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  useEffect(() => {
    reload();
  }, [reload]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);

    const dollars = parseFloat(amountDollars);
    if (!(dollars >= 0)) {
      setFormError('Amount must be a non-negative number.');
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(paymentDate)) {
      setFormError('Payment date must be in YYYY-MM-DD format.');
      return;
    }
    if (!description.trim()) {
      setFormError('Description is required for the audit trail.');
      return;
    }

    const amount_cents = Math.round(dollars * 100);

    setSubmitting(true);
    try {
      await api.post(`${BASE}/${reportId}`, {
        amount_cents,
        payment_date: paymentDate,
        reference: reference.trim(),
        description: description.trim(),
        undisputed,
      });
      setPaymentDate('');
      setAmountDollars('');
      setReference('');
      setDescription('');
      setUndisputed(true);
      await reload();
    } catch (err: any) {
      setFormError(err?.message || 'Save failed.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(id: string) {
    try {
      await api.delete(`${BASE}/${reportId}/${id}`);
      await reload();
    } catch (err: any) {
      setFormError(err?.message || 'Delete failed.');
    }
  }

  const runningTotalDollars = useMemo(() => {
    // Thousands separator + 2dp so the running total matches the controversy
    // panel's figures ($40,000.00, not $40000.00) — K3 review 2026-08-24.
    return ((data?.total_cents ?? 0) / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, [data?.total_cents]);

  if (loading && !data) {
    return (
      <div className="p-6 flex items-center gap-2" style={{ color: 'var(--color-text-muted)' }}>
        <Loader2 size={16} className="animate-spin" />
        Loading carrier advances…
      </div>
    );
  }

  if (loadError) {
    return (
      <div
        role="alert"
        className="m-6 p-4"
        style={{
          borderRadius: 2,
          border: '1px solid var(--color-blocker-tint-bd)',
          backgroundColor: 'var(--color-blocker-tint)',
          color: 'var(--color-blocker)',
        }}
      >
        <AlertCircle className="inline mr-2" size={18} />
        {loadError}
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="carrier-advance-form">
      {/* Header + intro */}
      <div>
        <h2 className="cs-h2">
          Carrier Advances
        </h2>
        <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>
          Undisputed carrier advances / partial payments against this
          claim. Entered here, not in chat — every dollar is audited.
          These NEVER change the filed claim or the net-claim
          calculation; they only feed the derived figure below.
        </p>
      </div>

      {/* ── Amount in Controversy — derived, read-only ── */}
      <div
        className="p-4"
        style={{
          borderRadius: 2,
          border: '1px solid var(--color-border)',
          backgroundColor: 'var(--color-bg-elevated)',
        }}
        data-testid="amount-in-controversy-panel"
      >
        <div className="flex items-center gap-2 mb-3">
          <Scale size={14} style={{ color: 'var(--color-text-muted)' }} />
          <h3
            className="text-xs font-semibold uppercase tracking-wide"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            Amount in Controversy — derived, not the filed claim
          </h3>
        </div>

        <dl className="space-y-2">
          <div className="flex items-center justify-between">
            <dt className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Net claim (per engine)
            </dt>
            <dd data-testid="controversy-net-claim">
              <FinancialValue value={controversy?.net_claim ?? null} size="sm" />
            </dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Less: carrier advances (undisputed)
            </dt>
            <dd data-testid="controversy-advances-total">
              <FinancialValue
                value={controversy?.advances_total ?? 0}
                size="sm"
                contra
                adverse
              />
            </dd>
          </div>
          <div
            className="flex items-center justify-between pt-2 mt-1"
            style={{ borderTop: '1px solid var(--color-border-faint)' }}
          >
            <dt className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
              = Amount in Controversy
            </dt>
            <dd data-testid="controversy-amount">
              <FinancialValue
                value={controversy?.amount_in_controversy ?? null}
                size="lg"
              />
            </dd>
          </div>
        </dl>

        <p className="text-xs mt-3 italic" style={{ color: 'var(--color-text-dim)' }}>
          Derived for negotiation tracking only — max(0, net claim −
          advances). Does not modify the filed gross claim or the
          deduction-waterfall net claim.
        </p>
      </div>

      {/* Entry form */}
      <form
        onSubmit={handleSubmit}
        className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3"
        style={{ borderRadius: 2, border: '1px solid var(--color-border)' }}
      >
        <label className="block">
          <span className="block text-xs mb-1" style={{ color: 'var(--color-text-secondary)' }}>Payment date</span>
          <input
            type="date"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            required
            data-testid="input-payment-date"
            className="w-full px-2 py-1.5 text-sm focus:outline-none"
            style={{ borderRadius: 2, border: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-surface)', color: 'var(--color-text-primary)' }}
          />
        </label>

        <label className="block">
          <span className="block text-xs mb-1" style={{ color: 'var(--color-text-secondary)' }}>Amount ($)</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={amountDollars}
            onChange={(e) => setAmountDollars(e.target.value)}
            required
            data-testid="input-amount"
            className="w-full px-2 py-1.5 text-sm text-right font-mono focus:outline-none"
            style={{ borderRadius: 2, border: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-surface)', color: 'var(--color-text-primary)' }}
          />
        </label>

        <label className="block">
          <span className="block text-xs mb-1" style={{ color: 'var(--color-text-secondary)' }}>Reference #</span>
          <input
            type="text"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            data-testid="input-reference"
            placeholder="Check / wire #"
            className="w-full px-2 py-1.5 text-sm focus:outline-none"
            style={{ borderRadius: 2, border: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-surface)', color: 'var(--color-text-primary)' }}
          />
        </label>

        <label className="block">
          <span className="block text-xs mb-1" style={{ color: 'var(--color-text-secondary)' }}>Description</span>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            data-testid="input-description"
            placeholder="Audit-trail note"
            className="w-full px-2 py-1.5 text-sm focus:outline-none"
            style={{ borderRadius: 2, border: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-surface)', color: 'var(--color-text-primary)' }}
          />
        </label>

        <label className="flex items-center gap-2 md:col-span-4">
          <input
            type="checkbox"
            checked={undisputed}
            onChange={(e) => setUndisputed(e.target.checked)}
            data-testid="input-undisputed"
            style={{ accentColor: 'var(--color-verified)' }}
          />
          <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
            The carrier's advance amount itself is undisputed
          </span>
        </label>

        <div className="md:col-span-4 flex items-center justify-between">
          <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
            Stored in cents · never anchors the filed claim
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="submit-advance"
            className="flex items-center gap-2 px-4 py-1.5 text-sm font-medium disabled:cursor-not-allowed"
            style={{ borderRadius: 2, backgroundColor: submitting ? 'var(--color-text-ghost)' : 'var(--color-brand)', color: 'var(--color-on-brand)' }}
          >
            {submitting ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Plus size={14} />
            )}
            Record advance
          </button>
        </div>

        {formError && (
          <div
            role="alert"
            data-testid="form-error"
            className="md:col-span-4 p-2 text-xs"
            style={{
              borderRadius: 2,
              border: '1px solid var(--color-blocker-tint-bd)',
              backgroundColor: 'var(--color-blocker-tint)',
              color: 'var(--color-blocker)',
            }}
          >
            {formError}
          </div>
        )}
      </form>

      {/* List of existing advances */}
      <div style={{ borderRadius: 2, border: '1px solid var(--color-border)', overflow: 'hidden' }}>
        <div className="px-4 py-2 flex items-center justify-between" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
          <h3 className="cs-h3">
            Advances on file
          </h3>
          <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
            Running total:{' '}
            <span className="font-mono">${runningTotalDollars}</span>
          </span>
        </div>

        {data && data.advances.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs border-b" style={{ color: 'var(--color-text-muted)', borderColor: 'var(--color-border)' }}>
                <th className="px-4 py-2 font-medium">Date</th>
                <th className="px-4 py-2 font-medium">Reference</th>
                <th className="px-4 py-2 font-medium text-right">Amount</th>
                <th className="px-4 py-2 font-medium">Description</th>
                <th className="px-4 py-2 font-medium">Undisputed</th>
                <th className="px-4 py-2 font-medium">Entered by</th>
                <th className="px-4 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.advances.map((a) => (
                <tr
                  key={a.advance_id}
                  className="border-b last:border-b-0"
                  style={{ borderColor: 'var(--color-border-faint)' }}
                  data-testid={`advance-row-${a.advance_id}`}
                >
                  <td className="px-4 py-2 font-mono text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                    {a.payment_date}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                    {a.reference || '—'}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <FinancialValue value={a.amount_cents / 100} size="sm" />
                  </td>
                  <td className="px-4 py-2 text-xs max-w-[32ch] truncate" style={{ color: 'var(--color-text-secondary)' }}>
                    {a.description}
                  </td>
                  <td className="px-4 py-2 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                    {a.undisputed ? 'Yes' : 'No'}
                  </td>
                  <td className="px-4 py-2 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                    {a.author_name}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => handleDelete(a.advance_id)}
                      className="p-1"
                      style={{ color: 'var(--color-text-dim)' }}
                      title="Delete advance"
                      data-testid={`delete-${a.advance_id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="px-4 py-6 text-sm" style={{ color: 'var(--color-text-dim)' }}>
            No carrier advances entered yet.
          </p>
        )}
      </div>
    </div>
  );
}
